import { useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { Flag, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";

const REASONS = [
  "Sexual or explicit content",
  "Violence or threats",
  "Slurs or hate speech",
  "Harassment or bullying",
  "Scam or fraud",
  "Other",
];

export function ReportButton({ targetType, targetId, variant = "icon", className = "" }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!reason) return toast.error("Please pick a reason");
    setBusy(true);
    try {
      await api.post("/reports", { target_type: targetType, target_id: targetId, reason: note ? `${reason} — ${note}` : reason });
      toast.success("Thanks for reporting. Our team will review it.");
      setOpen(false); setReason(""); setNote("");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <>
      {variant === "icon" ? (
        <button type="button" data-testid={`report-${targetType}-${targetId}`} aria-label="Report"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}
          className={`w-8 h-8 rounded-full flex items-center justify-center text-[#9CA3AF] hover:text-[#B23B3B] hover:bg-[#FBE9E9]/60 transition-colors ${className}`}>
          <Flag size={15} />
        </button>
      ) : (
        <button type="button" data-testid={`report-${targetType}-${targetId}`}
          onClick={(e) => { e.preventDefault(); setOpen(true); }}
          className={`inline-flex items-center gap-1.5 text-sm font-semibold text-[#9CA3AF] hover:text-[#B23B3B] transition-colors ${className}`}>
          <Flag size={14} /> Report
        </button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-3xl" data-testid="report-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Report this {targetType}</DialogTitle>
            <DialogDescription>Help keep Errandell safe. Your report is confidential.</DialogDescription>
          </DialogHeader>
          <div className="flex flex-wrap gap-2 py-1">
            {REASONS.map((r) => (
              <button key={r} type="button" data-testid={`report-reason-${r.split(" ")[0].toLowerCase()}`} onClick={() => setReason(r)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border-2 transition-colors ${reason === r ? "border-primary bg-primary/5 text-primary" : "border-gray-200 text-[#6B7280]"}`}>
                {r}
              </button>
            ))}
          </div>
          <Textarea data-testid="report-note-input" value={note} onChange={(e) => setNote(e.target.value)} rows={3}
            placeholder="Add any details (optional)…" className="rounded-2xl border-gray-200" />
          <DialogFooter>
            <Button variant="outline" data-testid="report-cancel-btn" onClick={() => setOpen(false)} className="rounded-full font-semibold">Cancel</Button>
            <Button data-testid="report-submit-btn" onClick={submit} disabled={busy} className="rounded-full font-bold bg-[#B23B3B] hover:bg-[#9c3333]">
              {busy ? <Loader2 className="animate-spin" size={16} /> : "Submit report"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
